/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import {
  ItemActionDetail,
  ItemFocusDetail,
  ItemHoverDetail,
  ItemInfo,
  LegendItemData
} from '#utils/UNSAFE_visTypes/legend';

function getItem<K, D extends LegendItemData<K>>(itemInfo: ItemInfo, items: D[]) {
  const { itemIdx } = itemInfo;

  if (itemIdx == -1) {
    return items[items.length - 1];
  }
  return items[itemIdx];
}

function getNextItemInfo<K>(itemInfo: ItemInfo, items: LegendItemData<K>[]) {
  let itemIdx = itemInfo.itemIdx;

  if (itemIdx == -1) {
    itemIdx = items.length - 1;
  }

  itemIdx = Math.min(items.length - 1, itemIdx + 1);
  if (items[itemIdx].actionable !== 'off') {
    return { sectionIdx: 0, itemIdx };
  } else if (itemIdx === items.length - 1) {
    itemIdx = itemIdx - 1;
    return { sectionIdx: 0, itemIdx };
  }

  return getNextItemInfo({ sectionIdx: 0, itemIdx }, items);
}

function getPrevItemInfo<K>(itemInfo: ItemInfo, items: LegendItemData<K>[]) {
  let itemIdx = itemInfo.itemIdx;

  if (itemIdx == -1) {
    itemIdx = items.length - 1;
  }

  itemIdx = Math.max(0, itemIdx - 1);
  if (items[itemIdx].actionable !== 'off') {
    return { sectionIdx: 0, itemIdx };
  }

  return getPrevItemInfo({ sectionIdx: 0, itemIdx }, items);
}

/**
 * Returns utils functions for navigation in simple legend.
 * @param items
 * @returns
 */
export function getLegendNavUtils<K extends string | number, D extends LegendItemData<K>>(
  items: D[]
) {
  return {
    getItem: (itemInfo: ItemInfo) => {
      return getItem(itemInfo, items);
    },
    getDetailFromInfo: (
      itemInfo?: ItemInfo
    ): ItemFocusDetail<K, D> | ItemHoverDetail<K, D> | ItemActionDetail<K, D> => {
      if (!itemInfo || items.length === 0) {
        return { itemId: undefined, data: undefined };
      }
      const item = getItem(itemInfo, items);
      return { itemId: item.id, data: item };
    },
    getPrevItemInfo: (itemInfo: ItemInfo) => {
      return getPrevItemInfo(itemInfo, items);
    },
    getNextItemInfo: (itemInfo: ItemInfo) => {
      return getNextItemInfo(itemInfo, items);
    }
  };
}
